/* eslint-disable max-len */
import { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as repr } from '@sitebuilder/core';
import { actionCreators, resourcesReducerActions } from '../reducers/resources';
import { actionCreators as templateEditorActionCreators } from '../reducers/templateEditor';
import { convertArrayToObject } from '../TemplateEditor/Toolbar';
import { StateShape as ReduxStateShape } from '../reducers';
import { resourceActionOptions } from './Layouts/LeftSidebar/NavOptions';
import { ResourcesTable, SideBarAccordion } from './BaseComponents';

interface GroupProperty {
    name: string;
    children: Array<ReactElement<any, string>>;
}

export function deletePropertiesNotInArray<T extends Record<string, any>>(
    obj: T,
    keysArray: string[]
): any {
    const object: any = {};
    for (const key in obj) {
        if (keysArray.includes(key)) {
            object[key] = obj[key]; // Delete the property if the key is not in the keysArray
        }
    }
    return object;
}

// Custom Field Template for grouping
export function ObjectFieldTemplate(props: any) {
    const { TitleField, DescriptionField, isTheme, onlyDataGrouping } = props;
    let topLevelGrouping: any = {};
    let title: string = props.title;
    let isAccordionOpen = false;

    if (isTheme) {
        topLevelGrouping = {
            Colors: ['Default Colors', 'Colors'],
            Typography: ['Default Typography', 'Typography'],
            Fonts: ['Default Fonts', 'Fonts'],
            Components: ['Components'],
            'Color Palettes': ['Default Color Palettes', 'Color Palettes'],
        };
    } else if (onlyDataGrouping) {
        topLevelGrouping = {
            Data: ['Dynamic Properties', 'Generated Properties'],
        };
    } else {
        topLevelGrouping = {
            Meta: [
                'Data Resources',
                'Annotations',
                'Meta Data',
                'Component Info',
                'Cache Control',
                'Page Analytics',
                'Analytics',
                'Resources',
            ],
            Style: ['Style Editor', 'Theme', 'Style', 'Text', 'Layout'],
            Data: [
                'Values',
                'Email Button',
                'Copy Button',
                'Selected List',
                'Transform Data',
                'Filters Options',
                'Query Options',
                'Download Options',
                'Accessibility',
                'Dynamic Properties',
                'Generated Properties',
            ],
        };
    }

    const groups =
        props?.uiSchema?.['ui:groups']?.length > 0
            ? Object.keys(props?.uiSchema?.['ui:groups']?.[0])
            : [];
    const otherGroups = groups?.filter(
        (d) =>
            !Object.values(topLevelGrouping)
                .flatMap((values) => values)
                .includes(d)
    );

    if (otherGroups.length > 0 && !isTheme) {
        topLevelGrouping.Other = otherGroups;
    }

    const forBottomBar = props.formContext.bottomBar;

    if (forBottomBar) {
        const keyToInclude = props.formContext.bottomBarKey;
        const selectedGroups = Object.keys(topLevelGrouping).filter((t) => t === keyToInclude);
        topLevelGrouping = deletePropertiesNotInArray(topLevelGrouping, selectedGroups);
        title = '';
        isAccordionOpen = true;
    }

    return (
        <fieldset>
            {(props.uiSchema?.['ui:title'] || title) && (
                <TitleField
                    id={`${props.idSchema.$id}__title`}
                    title={props.title || props.uiSchema?.['ui:title']}
                    required={props.required}
                />
            )}
            {props?.description && (
                <DescriptionField
                    id={`${props.idSchema.$id}__description`}
                    description={props?.description}
                />
            )}
            {props?.uiSchema?.['ui:groups']?.length > 0 ? (
                Object.keys(topLevelGrouping).map((name: string, index: number) => (
                    <>
                        {
                            <SideBarAccordion
                                eventKey={`${index}key`}
                                toggleTitle={name || ''}
                                isExpandable={
                                    !forBottomBar &&
                                    ['Meta', 'Style', 'Data', 'Other'].includes(name)
                                }
                                defaultActiveKey={isAccordionOpen ? `${index}key` : undefined}
                            >
                                {doGrouping({
                                    properties: props.properties,
                                    groups: props.uiSchema?.['ui:groups']?.map((d: any) => {
                                        const groupsObj = deletePropertiesNotInArray(
                                            d,
                                            topLevelGrouping[name]
                                        );
                                        return groupsObj;
                                    }),
                                    props: props,
                                })}
                            </SideBarAccordion>
                        }
                    </>
                ))
            ) : (
                <div style={{ margin: '1rem 0.5rem' }}>
                    {props?.properties?.map((p: any) => p.content)}
                </div>
            )}
        </fieldset>
    );
}

// Organize widget properties into groups
export function doGrouping({
    properties,
    groups,
    props,
}: {
    properties: any;
    groups: any;
    props: any;
}) {
    const mapped = groups?.map((g: string) => {
        const resourceStrings = convertArrayToObject(
            props.formData.resourceStrings
        ) as repr.Resource;
        const [activeTab, setActiveTab] = useState(0);
        const [tableResources, setTableResources] = useState<repr.Resource | null>(resourceStrings);

        const dispatch = useDispatch();
        const { pageResourcesAction } = useSelector((state: ReduxStateShape) => ({
            pageResourcesAction: state.templateEditor?.pageResourcesAction,
        }));

        const handleSaveResources = (_tableResources: any, resources: any) => {
            dispatch(
                actionCreators.saveResources(_.merge({}, resources, _tableResources), 'page', false)
            );
        };

        if (typeof g === 'string') {
            const found = properties.filter(
                (p: { name: string | null | undefined }) => p.name === g
            );

            if (found.length === 1) {
                const el = found[0];
                return el.content;
            }
        } else if (typeof g === 'object') {
            let _properties = Object.keys(g).reduce((acc: any, key) => {
                const field = g[key];

                if (key.startsWith('ui:')) return acc;

                if (!Array.isArray(field)) return acc;

                return [
                    ...acc,
                    {
                        name: key,
                        children: doGrouping({
                            properties,
                            props,
                            groups: field,
                        }),
                    },
                ];
            }, []);

            _properties = _properties?.filter(
                (p: GroupProperty) => p.children.filter((c) => c).length > 0
            );

            return (
                <div>
                    <ul className="accordian-tabs" role="tablist">
                        {_properties.map((p: { name: string }, index: number) => (
                            <li
                                className={`nav-item ${activeTab === index && 'active'}`}
                                key={index}
                                onClick={() => setActiveTab(index)}
                            >
                                {p.name}
                            </li>
                        ))}
                    </ul>
                    <div className="tab-content">
                        {_properties.map(
                            (p: { name: string; children: React.ReactNode }, index: number) =>
                                activeTab === index ? (
                                    <div
                                        className={`tab-pane fade${
                                            index === 0 ? ' show active' : ''
                                        }`}
                                        id={`tabpanel-${index}`}
                                        key={`tabpanel-${index}`}
                                    >
                                        {p.name === 'Resources' && props.formContext.bottomBar ? (
                                            <>
                                                <div className={'page-resources-add-icons'}>
                                                    {resourceActionOptions.map((o) => (
                                                        <div
                                                            onClick={() => {
                                                                dispatch(
                                                                    templateEditorActionCreators.setPageAction(
                                                                        o.name
                                                                    )
                                                                );
                                                                dispatch(
                                                                    // eslint-disable-next-line max-len
                                                                    actionCreators.setSelectedResource(
                                                                        null
                                                                    )
                                                                );
                                                                dispatch(
                                                                    resourcesReducerActions.RESOURCES_SAVE_END(
                                                                        {
                                                                            success: '',
                                                                        }
                                                                    )
                                                                );
                                                            }}
                                                            key={o.name}
                                                        >
                                                            <img
                                                                src={
                                                                    pageResourcesAction === o.name
                                                                        ? o.activeIcon
                                                                        : o.icon
                                                                }
                                                                alt={o.name}
                                                                className={
                                                                    'main-left-sidebar-icon icon'
                                                                }
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <ResourcesTable
                                                    resources={resourceStrings}
                                                    resourcesLoading={false}
                                                    resourcesLoadError={null}
                                                    resourcesType={'page'}
                                                    fullWidth={true}
                                                    tableResources={tableResources}
                                                    setTableResources={(r) => {
                                                        setTableResources(r);
                                                        handleSaveResources(r, resourceStrings);
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            p.children
                                        )}
                                    </div>
                                ) : (
                                    <></>
                                )
                        )}
                    </div>
                </div>
            );
        }

        return '';
    });

    return mapped;
}
